import { Injectable } from '@angular/core';
import { TicketPriceDTO, TicketPriceService} from 'ticket-sdk';
import { Storage } from '@ionic/storage';
import {BehaviorSubject, forkJoin, from, mapTo, Observable, Subject, of} from 'rxjs';
import {first, map, switchMap, take, tap} from 'rxjs/operators';
import { SimpleOrder } from '@app/shared/interfaces/simple-order.interface';
import { SimpleOrderItem } from '@app/shared/interfaces/simple-order-item.interface';

@Injectable({
  providedIn: 'root'
})
export class LatestPurchasesService {
  latestPurchases: BehaviorSubject<SimpleOrder[]> = new BehaviorSubject([]);

  private maxStoredOrders = 5;

  constructor(private storage: Storage, private ticketPriceService: TicketPriceService) {
    from(this.storage.ready()).pipe(
      take(1),
      switchMap(() => from(this.storage.get('latest-purchases')).pipe(map(latestPurchases => {
        if (!!latestPurchases) {
          latestPurchases = latestPurchases.filter(order => this.isValidSimpleOrder(order)); // Remove old OrderDTO objects and keep SimpleOrder objects
          this.latestPurchases.next(latestPurchases);
          this.storage.set('latest-purchases', latestPurchases);
        }
      })))
    ).subscribe();
  }

  getLatestPurchases(): BehaviorSubject<SimpleOrder[]> {
    return this.latestPurchases;
  }

  getOrderIndex(order: SimpleOrder): Observable<number> {
    return this.getLatestPurchases().pipe(map(latestPurchases => {
      for (let i = 0; i < latestPurchases.length; i++) {
        let isOrderIdentical = false;
        const comparedOrder = latestPurchases[i];
        
        if (comparedOrder.simpleOrderItems.length === order.simpleOrderItems.length) {
          let identicalOrderItems: number = 0;
  
          for (let j = 0; j < comparedOrder.simpleOrderItems.length; j++) {
            const orderItem = comparedOrder.simpleOrderItems[j];
            const newOrderItem = order.simpleOrderItems[j];
            
            if (orderItem.licensePlateNumber === newOrderItem.licensePlateNumber 
                && orderItem.ticketPrice.ticketType.name === newOrderItem.ticketPrice.ticketType.name
                && orderItem.ticketPrice.vehicleType.vehicleTypeId === newOrderItem.ticketPrice.vehicleType.vehicleTypeId
            ) {
              identicalOrderItems++;
            }
          }
  
          if (identicalOrderItems === comparedOrder.simpleOrderItems.length) {
            isOrderIdentical = true;
          }
        }
  
        if (isOrderIdentical) {
          return i;
        }
      }

      return -1;
    }));
  }

  isValidSimpleOrder(obj: any): obj is SimpleOrder {
    if ('simpleOrderItems' in obj) {
      let validOrderItems = true;
      for (const simpleOrderItem of obj.simpleOrderItems) {
        if (!this.isValidSimpleOrderItem(simpleOrderItem)) {
          validOrderItems = false;
          break;
        }
      }

      return validOrderItems;
    }
    else {
      return false;
    }
  }

  isValidSimpleOrderItem(obj: any): obj is SimpleOrderItem {
    return 'ticketPrice' in obj &&
      'licensePlateNumber' in obj && typeof obj.licensePlateNumber === 'string';
  }

  onNewPurchase(simpleOrderItems: SimpleOrderItem[]) {
    if (simpleOrderItems.length > 0) {
      const simpleOrder = { simpleOrderItems }

      // Check if order is unique and add it to latest purchases if it is
      this.getOrderIndex(simpleOrder)
      .pipe(take(1))
      .subscribe(index => {
        if (index === -1) {
          let newLatestPurchases: SimpleOrder[] = [ ...this.latestPurchases.value ];
          if (newLatestPurchases.length >= this.maxStoredOrders) {
            while (newLatestPurchases.length >= this.maxStoredOrders) {
              newLatestPurchases.pop();
            }
          }
      
          newLatestPurchases.unshift(simpleOrder);
          
          this.latestPurchases.next(newLatestPurchases);
          this.storage.set('latest-purchases', newLatestPurchases);          
        }
      })
    }
  }

  removeOrder(order: SimpleOrder) {
    this.getOrderIndex(order).pipe(first()).subscribe(index => {
      const latestPurchases = [...this.latestPurchases.value];

      latestPurchases.splice(index, 1)

      this.latestPurchases.next(latestPurchases);
      this.storage.set('latest-purchases', latestPurchases); 
    });
  }

  updateSimpleOrderItemsWithCurrentPrices(simpleOrderItems: SimpleOrderItem[]): Observable<SimpleOrderItem[]> {
    if (simpleOrderItems.length === 0) {
        return of([]);
    }
    
    // Create an array of unique vehicle type ids
    const uniqueVehicleTypeIds = [...new Set(simpleOrderItems.map(simpleOrderItem => simpleOrderItem.ticketPrice.vehicleType.vehicleTypeId))]; 

    const observables = uniqueVehicleTypeIds.map(vehicleTypeId => {
      return this.ticketPriceService.getSingleTicketPriceByVehicleType(vehicleTypeId).pipe(
        first(),
        tap(ticketPrices => {
          simpleOrderItems.filter(simpleOrderItem => simpleOrderItem.ticketPrice.vehicleType.vehicleTypeId === vehicleTypeId)
          .forEach(simpleOrderItem => {
            const ticketPrice: TicketPriceDTO = ticketPrices.find(ticketPrice => {
              return ticketPrice.ticketPriceId === simpleOrderItem.ticketPrice.ticketPriceId || 
                (ticketPrice.ticketType.ticketTypeId === simpleOrderItem.ticketPrice.ticketType.ticketTypeId && ticketPrice.quantity === simpleOrderItem.ticketPrice.quantity);
            });
            
            if (ticketPrice) {
              simpleOrderItem.price = ticketPrice.price;
              simpleOrderItem.ticketPrice = ticketPrice;
            }
          });
        })
      );
    });

    return forkJoin(observables).pipe(
      mapTo(simpleOrderItems)
    );
  }
}
